import { useMemo, useState } from "react";

import LoadingOverlay from "../../../shared/components/LoadingOverlay";

import PageHeader from "../../../shared/components/PageHeader";

import { NotificationSettings } from "../../notifications/components/NotificationSettings";

import type { QuotaPeriod } from "../api/settingsApi";

import {
  useCronSettingsQuery,
  useUpdateCronFrequency,
} from "../hooks/useMaintenanceSettings";

import { ExecutionsTable, QueueStatusCard, QuotasCard } from "./subcomponents";

const SettingsPage = () => {
  const [page, setPage] = useState(1);

  const pageSize = 10;

  const [walletFilter, setWalletFilter] = useState("");

  const [platformFilter, setPlatformFilter] = useState("all");

  const [statusFilter, setStatusFilter] = useState("all");

  const [sortBy, setSortBy] = useState<string>("executedAt");

  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [fromDate, setFromDate] = useState("");

  const [toDate, setToDate] = useState("");

  const [quotaPeriod, setQuotaPeriod] = useState<QuotaPeriod>("daily");

  const filters = {
    wallet: walletFilter || undefined,

    platform: platformFilter !== "all" ? platformFilter : undefined,

    success: statusFilter !== "all" ? statusFilter : undefined,

    from: fromDate || undefined,

    to: toDate || undefined,
  };

  const { data, isLoading, isFetching } = useCronSettingsQuery(
    page,

    pageSize,

    filters,

    sortBy,

    sortOrder,

    quotaPeriod,
  );

  const updateCronMutation = useUpdateCronFrequency();

  const [selectedFrequency, setSelectedFrequency] = useState<number | null>(
    null,
  );

  const currentFrequency = selectedFrequency ?? data?.selectedMinutes ?? null;

  const frequencies = data?.allowedMinutes ?? [];

  const executions = data?.executions ?? [];

  const totalPages = data
    ? Math.ceil(data.executionsTotal / data.executionsPageSize)
    : 0;

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);

      setSortOrder("desc");
    }

    setPage(1);
  };

  const handleFilterChange = () => {
    setPage(1);
  };

  const isBusy = isLoading || isFetching;

  const handleFrequencyChange = (value: number) => {
    setSelectedFrequency(value);
  };

  const handleApplyFrequency = async () => {
    if (!currentFrequency) return;

    await updateCronMutation.mutateAsync({ minutes: currentFrequency });

    setSelectedFrequency(null);
  };

  const isFrequencyDirty =
    selectedFrequency !== null && selectedFrequency !== data?.selectedMinutes;

  const frequencyLabel = useMemo(() => {
    if (!data?.selectedMinutes) return "No configurado";

    if (data.selectedMinutes % 60 === 0) {
      const hours = data.selectedMinutes / 60;

      return hours === 1 ? "Cada hora" : `Cada ${hours} horas`;
    }

    return `Cada ${data.selectedMinutes} minutos`;
  }, [data?.selectedMinutes]);

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat("es-AR", {
        dateStyle: "short",

        timeStyle: "short",
      }),

    [],
  );

  const handleClearFilters = () => {
    setWalletFilter("");

    setPlatformFilter("all");

    setStatusFilter("all");

    setFromDate("");

    setToDate("");

    handleFilterChange();
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Crons"
        subtitle="Controla la frecuencia de actualización automática de tokens de integraciones."
      />

      {/* Status cards */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <QueueStatusCard
          pendingJobs={data?.pendingJobs ?? 0}
          processedToday={data?.processedToday ?? 0}
        />
        <QuotasCard
          quotas={data?.quotas ?? []}
          quotaPeriod={quotaPeriod}
          onPeriodChange={setQuotaPeriod}
        />
      </section>

      {/* Executions table */}
      <ExecutionsTable
        executions={executions}
        dateFormatter={dateFormatter}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        walletFilter={walletFilter}
        platformFilter={platformFilter}
        statusFilter={statusFilter}
        fromDate={fromDate}
        toDate={toDate}
        onWalletFilterChange={(v) => {
          setWalletFilter(v);

          handleFilterChange();
        }}
        onPlatformFilterChange={(v) => {
          setPlatformFilter(v);

          handleFilterChange();
        }}
        onStatusFilterChange={(v) => {
          setStatusFilter(v);

          handleFilterChange();
        }}
        onFromDateChange={(v) => {
          setFromDate(v);

          handleFilterChange();
        }}
        onToDateChange={(v) => {
          setToDate(v);

          handleFilterChange();
        }}
        onClearFilters={handleClearFilters}
        page={page}
        totalPages={totalPages}
        totalExecutions={data?.executionsTotal ?? 0}
        onPageChange={setPage}
        currentFrequency={currentFrequency}
        frequencies={frequencies}
        frequencyLabel={frequencyLabel}
        isFrequencyDirty={isFrequencyDirty}
        isBusy={isBusy}
        isPending={updateCronMutation.isPending}
        onFrequencyChange={handleFrequencyChange}
        onApplyFrequency={handleApplyFrequency}
      />

      <NotificationSettings />

      {isBusy && <LoadingOverlay />}
    </div>
  );
};

export default SettingsPage;
